import React from 'react';
import { useTranslation } from 'react-i18next';
import { ClipboardList, Rocket, TrainFront, FlagTriangleRight, Check, ArrowRight } from 'lucide-react';
import { Transport } from '../../services/api';
import { formatUTC } from '../../utils/timeUtils';
import './TransportTimeline.css';

interface TransportTimelineProps {
  transport: Transport;
  currentStatus: string;
}

function TransportTimeline({ transport, currentStatus }: TransportTimelineProps) {
  const { t } = useTranslation();
  const stages = [
    {
      key: 'PLANNED',
      label: t('status.planned'),
      icon: <ClipboardList size={18} />,
      description: t('missions.createMission'),
      timestamp: transport.createdAt,
      completed: true
    },
    {
      key: 'DEPARTED',
      label: t('missions.departure'),
      icon: <Rocket size={18} />,
      description: `Departed from ${transport.startPoint?.name || 'Transport Hub'}`,
      timestamp: transport.departureTime,
      completed: currentStatus === 'IN_TRANSIT' || currentStatus === 'COMPLETED'
    },
    {
      key: 'IN_TRANSIT',
      label: t('status.inTransit'),
      icon: <TrainFront size={18} />,
      description: `En route to ${transport.hub?.name || 'Destination'}`,
      timestamp: transport.departureTime,
      completed: currentStatus === 'IN_TRANSIT' || currentStatus === 'COMPLETED',
      active: currentStatus === 'IN_TRANSIT'
    },
    {
      key: 'ARRIVED',
      label: t('missions.arrival'),
      icon: <FlagTriangleRight size={18} />,
      description: `Arrived at ${transport.hub?.name || 'Destination'}`,
      timestamp: transport.arrivalTime,
      completed: currentStatus === 'COMPLETED'
    }
  ];

  const formatTimestamp = (timestamp?: string) => {
    if (!timestamp) return t('status.pending');
    return formatUTC(timestamp, 'dd MMM yyyy HH:mm');
  };

  const getStageClass = (stage: typeof stages[0]) => {
    if (stage.active) return 'timeline-stage active';
    if (stage.completed) return 'timeline-stage completed';
    return 'timeline-stage pending';
  };

  return (
    <div className="transport-timeline">
      <h3 className="timeline-title">{t('missions.title')}</h3>

      <div className="timeline-container">
        {stages.map((stage, index) => (
          <div key={stage.key} className={getStageClass(stage)}>
            <div className="timeline-connector">
              {index < stages.length - 1 && (
                <div className={`connector-line ${stage.completed ? 'completed' : 'pending'}`} />
              )}
            </div>

            <div className="timeline-content">
              <div className="timeline-icon">
                <span className="icon">{stage.icon}</span>
                {stage.completed && <span className="check"><Check size={14} /></span>}
              </div>

              <div className="timeline-info">
                <h4 className="stage-label">{stage.label}</h4>
                <p className="stage-description">{stage.description}</p>
                <div className="stage-timestamp">
                  {stage.active && <span className="pulse-dot"></span>}
                  <span className="timestamp">{formatTimestamp(stage.timestamp)}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Transport Details Summary */}
      <div className="timeline-summary">
        <div className="summary-item">
          <label>{t('missions.asset')}</label>
          <span>{transport.asset?.name} ({transport.asset?.type})</span>
        </div>
        <div className="summary-item">
          <label>{t('map.routes')}</label>
          <span>{transport.startPoint?.name} <ArrowRight size={14} style={{ verticalAlign: 'middle' }} /> {transport.hub?.name}</span>
        </div>
        {transport.notes && (
          <div className="summary-item">
            <label>{t('common.notes')}</label>
            <span>{transport.notes}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default TransportTimeline;
